import React from 'react';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Modal, Button, Form, Toast, Spinner } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { fetchWithCSRF } from '../utils/csrfUtils';
import { getRecipeImageUrl, getIngredientImageUrl, getIconUrl, getS3ImageUrl } from '../utils/imageUtils';

import { useAddRecipeFavorite } from '../hooks/favoriteRecipe';
import { useDeleteRecipeFavorite } from '../hooks/favoriteRecipe';
import { useAddRecipeDislike, useDeleteRecipeDislike, useGetRecipeDislikeStatus } from '../hooks/dislikeRecipe';
import { useSupabaseRecipeImage } from '../hooks/getRecipeImage';
import { useAddRecipeToMealPlan } from '../hooks/useAddRecipeToMealPlan';
import AddToMealPlanModal from './AddToMealPlanModal';
import PantryMatchBar from './PantryMatchBar';
import { calculatePantryMatch } from '../utils/pantryMatchCalculator';



function RecipeCard({ recipe, isFavorite, isDisliked = false, pantryIngredients = null, onMealAdded }) {
  const { pgUser, loading: authLoading, sessionToken } = useAuth();
  const [isFavorited, setIsFavorited] = useState(isFavorite);
  const [showModal, setShowModal] = useState(false);
  const [currentImageSrc, setCurrentImageSrc] = useState(getRecipeImageUrl(recipe.id));
  const [shouldUseSupabase, setShouldUseSupabase] = useState(false);
  const supabaseImageUrl = useSupabaseRecipeImage(recipe.id, shouldUseSupabase);
  

  // New state for expanded view
  const [isExpanded, setIsExpanded] = useState(false);
  const [steps, setSteps] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // State for pantry match
  const [pantryMatchPercentage, setPantryMatchPercentage] = useState(null);

  // State for user's pantry ingredients (for ingredient status badges)
  const [userPantryIngredients, setUserPantryIngredients] = useState([]);

  // Add dislike hooks and state
  const { addRecipeDislike } = useAddRecipeDislike();
  const { deleteRecipeDislike } = useDeleteRecipeDislike();
  const [isDislikedState, setIsDislikedState] = useState(isDisliked);
  const [isTogglingDislike, setIsTogglingDislike] = useState(false);

  // Update currentImageSrc when supabaseImageUrl changes
  useEffect(() => {
    if (shouldUseSupabase) {
      if (supabaseImageUrl) {
        console.log('Using Supabase image for recipe:', recipe.id);
        setCurrentImageSrc(supabaseImageUrl);
      } else {
        // Supabase fetch failed or returned null, use placeholder
        console.log('Supabase image failed for recipe:', recipe.id, 'using placeholder');
        setCurrentImageSrc(getS3ImageUrl('images/recipeImages/placeholder_recipe.jpg'));
      }
    }
  }, [supabaseImageUrl, shouldUseSupabase, recipe.id]);

  const [servings, setServings] = useState(recipe.servings != null ? recipe.servings : 1);
  const [showFavoritedToast, setShowFavoritedToast] = useState(false);
  const [favoritedToastMessage, setFavoritedToastMessage] = useState('');

  //hook to add meal to meal plan
  const { addRecipeToMealPlan, isLoading, error } = useAddRecipeToMealPlan();

  //hook to add recipe to favorites
  const { addRecipeFavorite, isLoading: isAddingFavorite, error: addFavoriteError } = useAddRecipeFavorite();
  const { deleteRecipeFavorite, isLoading: isDeletingFavorite, error: deleteFavoriteError } = useDeleteRecipeFavorite();

  const handleFavoritedToast = (message) => {
    setShowFavoritedToast(true);
    setFavoritedToastMessage(message);
    setTimeout(() => {
      setShowFavoritedToast(false);
    }, 2000);
  };

  const handleModalClose = () => {
    setShowModal(false);
  };


  // Keep isFavorited state in sync with isFavorite prop
  useEffect(() => {
    setIsFavorited(isFavorite);
  }, [isFavorite]);

  // Keep isDislikedState in sync with isDisliked prop
  useEffect(() => {
    setIsDislikedState(isDisliked);
  }, [isDisliked]);

  // Handle toggling dislike status
  const handleToggleDislike = async (e) => {
    e.stopPropagation();
    if (!recipe?.id || isTogglingDislike) return;
    
    setIsTogglingDislike(true);
    try {
      if (isDislikedState) {
        const success = await deleteRecipeDislike(recipe.id);
        if (success) {
          setIsDislikedState(false);
          handleFavoritedToast('Removed from dislikes');
        }
      } else {
        const success = await addRecipeDislike(recipe.id);
        if (success) {
          setIsDislikedState(true);
          handleFavoritedToast('Added to dislikes');
        }
      }
    } catch (error) {
      console.error('Error toggling dislike:', error);
    } finally {
      setIsTogglingDislike(false);
    }
  };


  // Helper function to format quantity as clean fractions when possible
  const formatQuantity = (num) => {
    const allowedFractions = [
      { fraction: 1/16, label: "1/16", vulgar: "¹⁄₁₆" },
      { fraction: 1/8, label: "1/8", vulgar: "⅛" },
      { fraction: 1/6, label: "1/6", vulgar: "⅙" },
      { fraction: 1/4, label: "1/4", vulgar: "¼" },
      { fraction: 1/3, label: "1/3", vulgar: "⅓" },
      { fraction: 1/2, label: "1/2", vulgar: "½" },
      { fraction: 5/8, label: "5/8", vulgar: "⅝" },
      { fraction: 2/3, label: "2/3", vulgar: "⅔" },
      { fraction: 3/4, label: "3/4", vulgar: "¾" },
      { fraction: 5/6, label: "5/6", vulgar: "⅚" },
    ];

    const parsed = parseFloat(num);
    if (isNaN(parsed)) return num;

    const fractionTolerance = 0.008; // Tolerance to catch common rounding (e.g., 0.33 → 1/3, 0.66 → 2/3)
    const wholeTolerance = 0.02; // Larger tolerance for whole numbers (e.g., 0.99 → 1, 1.98 → 2)
    const whole = Math.floor(parsed);
    const decimal = parsed - whole;

    // Check if it's basically a whole number (including cases like 0.99 or 1.01)
    if (decimal < wholeTolerance || decimal > (1 - wholeTolerance)) {
      return `${decimal > (1 - wholeTolerance) ? whole + 1 : whole}`;
    }

    // Check for fraction match
    const match = allowedFractions.find(f => Math.abs(decimal - f.fraction) < fractionTolerance);

    if (match) {
      const fractionSpan = <span style={{ fontSize: '1.3em' }}>{match.vulgar}</span>;
      if (whole > 0) {
        return <>{whole} {fractionSpan}</>;
      }
      return fractionSpan;
    }

    // Fall back to decimal, cleaned up
    const rounded = parsed.toFixed(3);
    return rounded.replace(/\.00$/, '');
  };

  // Helper function to get ingredient status in user's pantry
  const getIngredientPantryStatus = (ingredientId, requiredQuantity, requiredUnit) => {
    // Filter pantry ingredients by matching ingredient_id
    const pantryMatches = userPantryIngredients.filter(
      pi => pi.ingredient_id === ingredientId
    );

    if (!pantryMatches || pantryMatches.length === 0) {
      return { 
        status: 'none', 
        badge: 'None in pantry', 
        color: '#e8e8e8',
        textColor: '#666',
        icon: 'x-solid-red.svg'
      };
    }

    // Sum up all quantities of this ingredient in the pantry
    // Note: This is a simple sum - in a more complex system you'd need unit conversion
    const totalPantryQuantity = pantryMatches.reduce((sum, item) => {
      return sum + (parseFloat(item.quantity) || 0);
    }, 0);

    if (totalPantryQuantity >= requiredQuantity) {
      return { 
        status: 'enough', 
        badge: 'Enough in pantry', 
        color: '#e8e8e8',
        textColor: '#166534',
        icon: 'checkmark-solid.svg'
      };
    } else if (totalPantryQuantity > 0) {
      return { 
        status: 'not_enough', 
        badge: 'Some in pantry', 
        color: '#e8e8e8',
        textColor: '#666',
        icon: 'dash-circle-fill.svg'
      };
    } else {
      return { 
        status: 'none', 
        badge: 'None in pantry', 
        color: '#e8e8e8',
        textColor: '#666',
        icon: 'x-solid-red.svg'
      };
    }
  };

  // Calculate pantry match percentage - only if recipe.ingredients already exists
  useEffect(() => {
    if (pgUser && pantryIngredients && recipe.ingredients && Array.isArray(recipe.ingredients)) {
      const percentage = calculatePantryMatch(pantryIngredients, recipe.ingredients);
      setPantryMatchPercentage(percentage);
    } else {
      setPantryMatchPercentage(null);
    }
  }, [pgUser, pantryIngredients, recipe.ingredients]);

  // Fetch recipe details when expanded
  useEffect(() => {
    const fetchRecipeDetails = async () => {
      if (!isExpanded || !sessionToken) return;
      
      setLoadingDetails(true);
      try {
        // Fetch recipe steps
        const stepsResponse = await fetchWithCSRF(`/api/v1/recipe-step/${recipe.id}`, {
          headers: {
            'Authorization': `Bearer ${sessionToken}`
          }
        });

        if (stepsResponse.ok) {
          const stepsData = await stepsResponse.json();
          if (Array.isArray(stepsData)) {
            setSteps(stepsData.sort((a, b) => a.step_number - b.step_number));
          }
        }

        // Fetch recipe ingredients for expanded view
        const ingredientsResponse = await fetchWithCSRF(`/api/v1/recipe-ingredient/recipe/${recipe.id}`, {
          headers: {
            'Authorization': `Bearer ${sessionToken}`
          }
        });

        if (ingredientsResponse.ok) {
          const ingredientsData = await ingredientsResponse.json();
          if (Array.isArray(ingredientsData)) {
            setIngredients(ingredientsData);
          }
        }

        // Fetch user's pantry ingredients (for status badges)
        if (pgUser?.pantry_id && pgUser?.id) {
          const pantryResponse = await fetchWithCSRF(`/api/v1/pantry-ingredient/pantry/${pgUser.pantry_id}/app_user/${pgUser.id}`, {
            headers: {
              'Authorization': `Bearer ${sessionToken}`
            }
          });

          if (pantryResponse.ok) {
            const pantryData = await pantryResponse.json();
            if (Array.isArray(pantryData)) {
              setUserPantryIngredients(pantryData);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching recipe details:', error);
      } finally {
        setLoadingDetails(false);
      }
    };

    fetchRecipeDetails();
  }, [isExpanded, recipe.id, sessionToken, pgUser?.pantry_id, pgUser?.id]);

  const handleModalShow = (e) => {
    e.stopPropagation(); // Prevent card click navigation
    setShowModal(true);
  };

  const handleCardClick = (e) => {
    // Don't expand if clicking the "Add to Meal Plan" button or heart icon
    if (e.target.closest('.add-to-meal-plan-btn') || e.target.closest('.heart-icon')) {
      return;
    }
    setIsExpanded(true);
  };

  const handleCollapse = () => {
    setIsExpanded(false);
  };

  const handleAddToMealPlan = async ({ selectedPlannedCookedDate, servings }) => {
    const result = await addRecipeToMealPlan({
      recipeId: recipe.id,
      servings: servings,
      plannedCookDate: selectedPlannedCookedDate
    });
    
    // Call the onMealAdded callback if provided and addition was successful
    if (result && onMealAdded) {
      onMealAdded();
    }
    
    return result;
  };
  
  const handleHeartClick = async (e) => {
    e.stopPropagation();
    if (isFavorited) {
      //delete favorite
      setIsFavorited(false);
      await deleteRecipeFavorite(recipe.id);
      handleFavoritedToast('Removed from favorites');
    } else {
      //add favorite
      setIsFavorited(true);
      await addRecipeFavorite(recipe.id);
      handleFavoritedToast('Added to favorites');
    }
  };

  // Render expanded view - using portal to escape transform containing block
  if (isExpanded) {
    return createPortal(
      <>
        {/* Backdrop */}
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9998,
          }}
          onClick={handleCollapse}
        />
        
        {/* Modal */}
        <div 
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            zIndex: 9999,
            borderRadius: '12px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            width: '100%',
            maxWidth: '1000px',
            overflowY: 'auto',
            overflowX: 'hidden',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
            touchAction: 'pan-y',
            WebkitOverflowScrolling: 'touch'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ position: 'relative' }}>
            {/* Close button */}
            <button
              onClick={handleCollapse}
              style={{
                position: 'absolute',
                top: '1rem',
                left: '1rem',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                zIndex: 10,
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                color: '#000'
              }}
            >
              ×
            </button>

            {/* Hero Image */}
            <div 
              style={{ 
                width: '100%',
                height: '200px',
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '12px 12px 0 0'
              }}
              className="recipe-modal-hero"
            >
              <img 
                src={currentImageSrc}
                onError={(e) => {
                  const placeholderUrl = getS3ImageUrl('images/recipeImages/placeholder_recipe.jpg');
                  if (currentImageSrc.includes('recipeImages/') && !shouldUseSupabase) {
                    console.log('S3 image failed for recipe:', recipe.id, 'trying Supabase');
                    setShouldUseSupabase(true);
                  } else if (currentImageSrc !== placeholderUrl) {
                    console.log('Using placeholder for recipe:', recipe.id);
                    e.target.onerror = null;
                    setCurrentImageSrc(placeholderUrl);
                  }
                }}
                alt={recipe.name}
                style={{ 
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center'
                }}
              />
              <div 
                style={{
                  position: 'absolute',
                  bottom: '1.5rem',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  color: 'white',
                  textAlign: 'center',
                  width: '100%',
                  padding: '0 1rem'
                }}
              >
                <h2 
                  style={{
                    fontSize: '2rem',
                    fontWeight: '600',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                    marginBottom: '0'
                  }}
                >
                  {recipe.name}
                </h2>
              </div>
            </div>
          </div>

          <div className="px-3 px-md-4 py-3">
            {/* Recipe Info Bar */}
            <div className="mb-3 pb-3" style={{ borderBottom: '1px solid #e0e0e0' }}>
              {/* Top row: Recipe stats - centered */}
              <div className="d-flex justify-content-center gap-3 gap-md-4 mb-3">
                <div className="text-center">
                  <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>Prep</small>
                  <span className="fw-bold" style={{ fontSize: '0.9rem' }}>{recipe.prep_time} min</span>
                </div>
                <div className="text-center">
                  <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>Cook</small>
                  <span className="fw-bold" style={{ fontSize: '0.9rem' }}>{recipe.cook_time} min</span>
                </div>
                {recipe.calories && (
                  <div className="text-center">
                    <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>Calories</small>
                    <span className="fw-bold" style={{ fontSize: '0.9rem' }}>{Math.round(parseFloat(recipe.calories))}</span>
                  </div>
                )}
              </div>
              
              {/* Bottom row: Servings and Add button */}
              <div className="d-flex justify-content-center align-items-center flex-wrap gap-3">
                <div className="d-flex flex-column align-items-center">
                  <div className="d-flex align-items-center gap-2">
                    <button 
                      className="btn btn-sm p-0" 
                      onClick={() => setServings(prev => Math.max(1, prev - 1))}
                      style={{ 
                        border: 'none', 
                        background: 'none',
                        color: '#52cd92',
                        fontSize: '1.2rem',
                        lineHeight: 1,
                        fontWeight: 'bold',
                        minWidth: '30px'
                      }}
                    >
                      −
                    </button>
                    <div className="text-center" style={{ minWidth: '50px' }}>
                      <span className="fw-bold" style={{ fontSize: '1.1rem' }}>{servings}</span>
                    </div>
                    <button 
                      className="btn btn-sm p-0" 
                      onClick={() => setServings(prev => Math.min(16, prev + 1))}
                      style={{ 
                        border: 'none', 
                        background: 'none',
                        color: '#52cd92',
                        fontSize: '1.2rem',
                        lineHeight: 1,
                        fontWeight: 'bold',
                        minWidth: '30px'
                      }}
                    >
                      +
                    </button>
                  </div>
                  <small className="text-muted" style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>
                    {servings === 1 ? 'serving' : 'servings'}
                  </small>
                </div>
                
                {pgUser && pgUser.setup_complete && pgUser.has_access && (
                  <div className="d-flex align-items-center gap-2">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleModalShow(e);
                      }}
                      className="btn btn-sm"
                      style={{ 
                        backgroundColor: '#52cd92',
                        borderColor: '#52cd92',
                        borderRadius: '6px',
                        padding: '0.5rem 1rem',
                        fontSize: '0.9rem'
                      }}
                    >
                      Add to Meal Plan
                    </button>
                    
                    {/* Favorite heart button */}
                    <img 
                      onClick={handleHeartClick}
                      src={getIconUrl(isFavorited ? 'filled-heart.svg' : 'heart-outline.svg')} 
                      // alt="Favorite" 
                      className="heart-icon"
                      style={{
                        width: '32px',
                        height: '32px',
                        cursor: 'pointer'
                      }}
                    />

                    {/* Dislike button */}
                    {/* <img 
                      onClick={handleToggleDislike}
                      src={getIconUrl(isDislikedState ? 'thumbDown-filled.svg' : 'thumbDown.svg')} 
                      alt="Dislike" 
                      style={{
                        width: '32px',
                        height: '32px',
                        cursor: 'pointer',
                        opacity: isTogglingDislike ? 0.5 : 1
                      }}
                      title={isDislikedState ? "Remove from dislikes" : "Dislike this recipe"}
                    /> */}
                  </div>
                )}
              </div>
            </div>

            {loadingDetails ? (
              <div className="text-center my-4">
                <Spinner animation="border" role="status" style={{ color: '#52cd92' }}>
                  <span className="visually-hidden">Loading recipe details...</span>
                </Spinner>
              </div>
            ) : (
              <div className="row g-3 g-md-4">
                <div className="col-12 col-md-5">
                  <div>
                    {/* Pantry Match Bar - only show for logged in users with data */}
                    {pantryMatchPercentage !== null && (
                      <div className="mb-3">
                        <PantryMatchBar percentage={pantryMatchPercentage} />
                      </div>
                    )}
                    
                    <h5 className="mb-3" style={{ fontSize: '1.1rem' }}>Ingredients</h5>
                    <div className="d-flex flex-column gap-2">
                        {ingredients.map((ingredient, index) => {
                          const adjustedQuantity = ingredient.quantity * (servings / (recipe?.servings || 1));
                          const pantryStatus = pgUser?.pantry_id ? 
                            getIngredientPantryStatus(ingredient.ingredient_id, adjustedQuantity, ingredient.unit_of_measurement) : 
                            null;
                          
                          return (
                            <div key={index} className="p-2 border rounded d-flex align-items-center">
                              <div 
                                style={{ 
                                  width: '40px',
                                  height: '40px',
                                  marginRight: '0.75rem',
                                  flexShrink: 0
                                }}
                              >
                                <img 
                                  src={getIngredientImageUrl(ingredient.ingredient_id)}
                                  alt={ingredient.ingredient_name}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: '4px',
                                  }}
                                  onError={(e) => {
                                    if (e.target.getAttribute('data-error-handled')) return;
                                    e.target.setAttribute('data-error-handled', 'true');
                                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHZpZXdCb3g9IjAgMCA1MCA1MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIGZpbGw9IiNFMEUwRTAiLz48cGF0aCBkPSJNMjAgMTVIMzBMMzIuNSAxNy41SDM3LjVDMzguODgxIDE3LjUgNDAgMTguNjE5IDQwIDIwVjM1QzQwIDM2LjM4MSAzOC44ODEgMzcuNSAzNy41IDM3LjVIMTIuNUMxMS4xMTkgMzcuNSAxMCAzNi4zODEgMTAgMzVWMjBDMTAgMTguNjE5IDExLjExOSAxNy41IDEyLjUgMTcuNUgxNy41TDIwIDE1Wk0yNSAzNUMyOC44NjYgMzUgMzIgMzEuODY2IDMyIDI4QzMyIDI0LjEzNCAyOC44NjYgMjEgMjUgMjFDMjEuMTM0IDIxIDE4IDI0LjEzNCAxOCAyOEMxOCAzMS44NjYgMjEuMTM0IDM1IDI1IDM1Wk0yNSAzMi41QzIyLjUxNSAzMi41IDIwLjUgMzAuNDg1IDIwLjUgMjhDMjAuNSAyNS41MTUgMjIuNTE1IDIzLjUgMjUgMjMuNUMyNy40ODUgMjMuNSAyOS41IDI1LjUxNSAyOS41IDI4QzI5LjUgMzAuNDg1IDI3LjQ4NSAzMi41IDI1IDMyLjVaIiBmaWxsPSIjOTA5MDkwIi8+PC9zdmc+';
                                  }}
                                />
                              </div>
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-baseline gap-2">
                                  <span className="fw-bold" style={{ fontSize: '0.9rem' }}>
                                    {formatQuantity(adjustedQuantity)} {ingredient.unit_of_measurement}
                                  </span>
                                  <span style={{ fontSize: '0.9rem' }}>{ingredient.ingredient_name}</span>
                                </div>
                                {pantryStatus && (
                                  <div style={{ marginTop: '0.25rem' }}>
                                    <span 
                                      className="badge d-flex align-items-center gap-1" 
                                      style={{ 
                                        backgroundColor: pantryStatus.color,
                                        color: pantryStatus.textColor,
                                        fontSize: '0.7rem',
                                        fontWeight: '500',
                                        padding: '0.25rem 0.5rem',
                                        whiteSpace: 'nowrap',
                                        width: 'fit-content'
                                      }}
                                    >
                                      <img 
                                        src={getIconUrl(pantryStatus.icon)} 
                                        alt="" 
                                        style={{ 
                                          width: '12px', 
                                          height: '12px'
                                        }} 
                                      />
                                      {pantryStatus.badge}
                                    </span>
                                  </div>
                                )}
                                {ingredient.notes && (
                                  <small className="text-muted d-block">{ingredient.notes}</small>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-md-7">
                  <div>
                    <h5 className="mb-3" style={{ fontSize: '1.1rem' }}>Recipe Steps</h5>
                    <div className="d-flex flex-column gap-3">
                        {steps.map((step) => (
                          <div key={step.id} className="card">
                            <div className="card-body d-flex gap-3">
                              <div>
                                <span 
                                  className="badge rounded-circle d-flex align-items-center justify-content-center" 
                                  style={{ 
                                    backgroundColor: '#52cd92',
                                    width: '28px',
                                    height: '28px',
                                    fontSize: '0.85rem',
                                    fontWeight: '600'
                                  }}
                                >
                                  {step.step_number}
                                </span>
                              </div>
                              <div className="flex-grow-1" style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>
                                {step.instruction}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Add to Meal Plan Modal */}
        <AddToMealPlanModal
          show={showModal}
          onHide={handleModalClose}
          recipe={recipe}
          servings={servings}
          setServings={setServings}
          onSubmit={handleAddToMealPlan}
          isLoading={isLoading}
          error={error}
          zIndex={10000}
        />

        {showFavoritedToast && (
          <Toast 
            show={showFavoritedToast} 
            onClose={() => setShowFavoritedToast(false)}
            style={{
              position: 'fixed',
              top: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10001,
              backgroundColor: '#52cd92',
              border: 'none',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              textAlign: 'center',
            }}
          >
            <strong style={{ color: 'white', padding: '10px 20px', display: 'block' }}>{favoritedToastMessage}</strong>
          </Toast>
        )}
      </>,
      document.body
    );
  }

  // Render card view
  return (
    <div 
      className="recipe-card" 
      onClick={handleCardClick}
      style={{ cursor: 'pointer', position: 'relative' }}
    >
      <h5>{recipe.name}</h5>
      <div style={{ position: 'relative' }}>
        <img 
          src={currentImageSrc}
          onError={(e) => {
            const placeholderUrl = getS3ImageUrl('images/recipeImages/placeholder_recipe.jpg');
            if (currentImageSrc.includes('recipeImages/') && !shouldUseSupabase) {
              // S3 image failed, try Supabase
              console.log('S3 image failed for recipe:', recipe.id, 'trying Supabase');
              setShouldUseSupabase(true);
            } else if (currentImageSrc !== placeholderUrl) {
              // Both S3 and Supabase failed (or Supabase not available), use placeholder
              console.log('Using placeholder for recipe:', recipe.id);
              e.target.onerror = null; // Prevent infinite loop
              setCurrentImageSrc(placeholderUrl);
            }
          }}
          alt={recipe.name}
          className="recipe-card-img"
          style={{
            borderRadius: '10px',
          }}
        />
        <img 
          onClick={handleHeartClick}
          src={getIconUrl(isFavorited ? 'filled-heart.svg' : 'unfilled-heart.svg')} 
          alt="Filled Heart"
          className="heart-icon"
          style={{
            position: 'absolute',
            top: '7.5px',
            right: '7.5px',
            maxHeight: '1.5em',
            width: '30px',
            // backgroundColor: 'rgba(255, 255, 255, 0.4)',
          }}
        />
      </div>

      {/* Pantry Match Bar - only show for logged in users with data */}
      {pantryMatchPercentage !== null && (
        <PantryMatchBar percentage={pantryMatchPercentage} />
      )}

      <p className="recipe-card-time" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <img src={"icons/clock.svg"} alt="" style={{ width: '16px', height: '16px' }} />
          {recipe.prep_time + recipe.cook_time} min
        </span>
        {recipe.calories != null && (
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <img src={"icons/fire.svg"} alt="" style={{ width: '16px', height: '16px' }} />
            {recipe.calories} kcal
          </span>
        )}
      </p>
      
      {!authLoading && pgUser && (
        <div className="d-flex gap-2">
        <button 
          className="btn btn-secondary" 
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(true);
          }}
          style={{ flex: 1, borderRadius: '24px' }}
        >
          View
        </button>
        <button 
          className="add-to-meal-plan-btn"
        onClick={(e) => {
          e.stopPropagation(); // Prevent card click when clicking the button
          handleModalShow(e);
        }}
        style={{ flex: 1, borderRadius: '24px !important' }}
      >
          Add
        </button>
        
        </div>
      )}

      <AddToMealPlanModal
        show={showModal}
        onHide={handleModalClose}
        recipe={recipe}
        servings={servings}
        setServings={setServings}
        onSubmit={handleAddToMealPlan}
        isLoading={isLoading}
        error={error}
      />

      {showFavoritedToast && (
        <Toast 
          show={showFavoritedToast} 
          onClose={() => setShowFavoritedToast(false)}
          style={{
            position: 'absolute',
            top: '2%',  // Position it in the middle of the card
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 100,
            backgroundColor: '#52cd92',
            border: 'none',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            width: '80%',
            marginTop: '10px'
          }}
        >
          {/* <Toast.Header style={{ 
            backgroundColor: '#52cd92', 
            borderBottom: '1px solid rgba(255,255,255,0.2)',
            display: 'flex',
            justifyContent: 'center',
            position: 'relative',
            width: '100%'
          }}> */}
            <strong style={{ color: 'white' }}>{favoritedToastMessage}</strong>
          {/* </Toast.Header> */}
        </Toast>
      )}
      
    </div>
  );
}

export default RecipeCard;